<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-[100]" @close="handleClickCancel">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              :class="
                isSuperAdmin ? 'sm:w-full sm:max-w-3xl' : 'sm:w-full sm:max-w-xl'
              "
              class="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8"
            >
              <!-- Header -->
              <div
                class="flex items-start justify-between gap-4 border-b border-untitled-gray-200 px-6 py-5"
              >
                <div class="flex items-start gap-3">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50"
                  >
                    <UserAddIcon class="h-5 w-5 text-emerald-700" />
                  </div>
                  <div>
                    <DialogTitle
                      as="h3"
                      class="text-lg font-semibold leading-6 text-untitled-gray-900"
                    >
                      Invite Users
                    </DialogTitle>
                    <p class="mt-1 text-sm text-untitled-gray-500">
                      Send an email invitation and assign a role to each
                      person you'd like to add.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  class="rounded-md p-1.5 text-untitled-gray-400 hover:bg-untitled-gray-100 hover:text-untitled-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  :disabled="isLoading"
                  @click="handleClickCancel"
                >
                  <span class="sr-only">Close</span>
                  <XIcon class="h-5 w-5" />
                </button>
              </div>

              <!-- Body -->
              <div class="max-h-[28rem] overflow-y-auto px-6 py-5" v-if="init">
                <div class="space-y-4">
                  <div
                    v-for="(user, i) in inviteUsers"
                    :key="i"
                    class="relative rounded-lg border border-untitled-gray-200 bg-untitled-gray-25 p-4"
                  >
                    <div class="mb-3 flex items-center justify-between">
                      <span
                        class="text-xs font-semibold uppercase tracking-wide text-untitled-gray-500"
                      >
                        Recipient {{ i + 1 }}
                      </span>
                      <button
                        v-if="inviteUsers.length > 1"
                        type="button"
                        @click="handleClickRemove(i)"
                        class="rounded-md p-1.5 text-untitled-gray-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <span class="sr-only">Remove recipient</span>
                        <TrashIcon class="h-4 w-4" />
                      </button>
                    </div>

                    <div
                      class="grid grid-cols-1 gap-4"
                      :class="
                        isSuperAdmin
                          ? 'sm:grid-cols-2 lg:grid-cols-4'
                          : 'sm:grid-cols-2'
                      "
                    >
                      <div class="text-left">
                        <label
                          class="mb-1 block text-sm font-medium text-untitled-gray-700"
                        >
                          Email address
                        </label>
                        <input
                          v-model="user.email"
                          type="text"
                          placeholder="example@domain.com"
                          class="block w-full rounded-md border border-untitled-gray-300 px-3 py-2 text-sm placeholder-untitled-gray-400 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-emerald-500"
                        />
                        <ErrorSpan v-if="showError && errorValue">
                          <span v-if="errorValue[`invite.${i}.email`]">
                            {{ errorValue[`invite.${i}.email`][0] }}
                          </span>
                        </ErrorSpan>
                      </div>

                      <div class="text-left">
                        <label
                          class="mb-1 block text-sm font-medium text-untitled-gray-700"
                        >
                          Role
                        </label>
                        <ECombobox
                          v-model="user.role_id"
                          :options="filteredRoles"
                          option-name="friendly_name"
                          value-key="id"
                          placeholder="Select Role"
                          :multiple="false"
                          :filterable="false"
                          :clearable="false"
                          @change="handleChangeRole(user)"
                        />
                        <ErrorSpan v-if="showError && errorValue">
                          <span v-if="errorValue[`invite.${i}.role_id`]">
                            {{ errorValue[`invite.${i}.role_id`][0] }}
                          </span>
                        </ErrorSpan>
                      </div>

                      <div v-if="isSuperAdmin" class="text-left">
                        <label
                          class="mb-1 block text-sm font-medium text-untitled-gray-700"
                        >
                          Company
                        </label>
                        <ECombobox
                          v-model="user.dealer_id"
                          :options="companies"
                          option-name="name"
                          value-key="id"
                          placeholder="Select company"
                          :multiple="false"
                          :filterable="false"
                          :clearable="false"
                          @change="handleChangeCompany($event, i, user)"
                        />
                        <ErrorSpan v-if="showError && errorValue">
                          <span v-if="errorValue[`invite.${i}.dealer_id`]">
                            {{ errorValue[`invite.${i}.dealer_id`][0] }}
                          </span>
                        </ErrorSpan>
                      </div>

                      <div
                        v-if="
                          isSuperAdmin ||
                          (user.role_id === dealerManager.id &&
                            !isSpecificManager) ||
                          (isMultiStoreManager &&
                            user.role_id === salesperson.id)
                        "
                        class="text-left"
                      >
                        <label
                          class="mb-1 block text-sm font-medium text-untitled-gray-700"
                        >
                          Store
                        </label>
                        <ECombobox
                          v-model="user.specific_dealer_id"
                          :options="user.specific_dealer_list"
                          option-name="name"
                          value-key="id"
                          placeholder="Select store"
                          :multiple="false"
                          :filterable="false"
                          :clearable="false"
                        />
                        <ErrorSpan v-if="showError && errorValue">
                          <span
                            v-if="errorValue[`invite.${i}.specific_dealer_id`]"
                          >
                            {{
                              errorValue[`invite.${i}.specific_dealer_id`][0]
                            }}
                          </span>
                        </ErrorSpan>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  class="mt-4 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-untitled-gray-300 px-3 py-2.5 text-sm font-medium text-untitled-gray-600 hover:border-emerald-500 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isLoading"
                  @click="handleClickAdd"
                >
                  <PlusIcon class="h-4 w-4" />
                  Add Another User
                </button>
              </div>

              <!-- Footer -->
              <div
                class="flex flex-col-reverse gap-3 border-t border-untitled-gray-200 bg-untitled-gray-25 px-6 py-4 sm:flex-row sm:justify-end"
              >
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-md border border-untitled-gray-300 bg-white px-4 py-2 text-sm font-medium text-untitled-gray-700 shadow-sm hover:bg-untitled-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isLoading"
                  @click="handleClickCancel"
                  ref="cancelButtonRef"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-md border border-transparent bg-emerald-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isLoading"
                  @click="handleClickSend"
                >
                  <LoadingSpinner :show="isLoading" />
                  Send Invites
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  computed,
  ref,
  watch,
  inject,
  onMounted,
} from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import {
  TrashIcon,
  PlusIcon,
  UserAddIcon,
  XIcon,
} from "@heroicons/vue/outline";
import Bugsnag from "@bugsnag/js";
import { useAuthStore } from "@/stores";
import { getRoles } from "@/composable/users/getRoles.js";
import { find as _find } from "lodash-es";
import { getCompanies } from "@/composable/companies/getCompanies";
import LoadingSpinner from "@/components/loaders/LoadingSpinner.vue";
import ECombobox from "@/components/element-components/ECombobox.vue";
import ErrorSpan from "@/components/utilities/ErrorSpan.vue";

const myRoles = getRoles();
const isSuperAdmin = myRoles.isSuperAdmin;
const isAccountManager = myRoles.isAccountManager;
const isSpecificManager = myRoles.isSpecificManager;
const isMultiStoreManager = myRoles.isMultiStoreManager;

const { fetchMainCompanies, companies } = getCompanies();

const authUser = useAuthStore();
const currentUser = computed(() => {
  return authUser.$state.user;
});

const axios = inject("axios");

const roles = ref([]);

const specificDealers = ref([]);

const init = ref(false);

const getRolesOption = async () => {
  await axios
    .get("/api/v3/roles")
    .then(({ data }) => {
      roles.value = data;
    })
    .catch((error) => {
      Bugsnag.notify(error);
    });
};

const getSpecificDealers = async (index = 0, dealerId = null) => {
  await axios
    .get("/api/v3/specific_dealers", {
      params: {
        dealer_id: dealerId || currentUser.value.dealer_id,
      },
    })
    .then(({ data }) => {
      if (specificDealers.value.length == 0) {
        specificDealers.value = data;
      }
      inviteUsers.value[index].specific_dealer_list = data;
    })
    .catch((error) => {
      Bugsnag.notify(error);
    });
};

onMounted(async () => {
  await getRolesOption();
  rolesCondition();

  inviteUsers.value = [
    {
      email: "",
      role_id: salesperson.value.id,
      dealer_id: currentUser.value.dealer_id,
      specific_dealer_id: currentUser.value.specific_dealer_id,
      specific_dealer_list: [],
    },
  ];

  await getSpecificDealers();
  await fetchMainCompanies();
  init.value = true;
});

const props = defineProps({
  open: {
    default: false,
    type: Boolean,
  },
  error: {
    type: Array,
  },
});

const emit = defineEmits(["cancelEvent", "sendEvent"]);

const open = computed(() => props.open);

const errorValue = computed(() => errorData.value || props.error);
const errorData = ref(null);
const showError = ref(false);
const isLoading = ref(false);
const inviteUsers = ref([]);

const superAdmin = computed(() => {
  return findRole("super-administrator");
});

const accountManager = computed(() => {
  return findRole("account-manager");
});

const secretShopper = computed(() => {
  return findRole("secret-shopper");
});

const dealerManager = computed(() => {
  return findRole("specific-dealer-manager");
});

const salesperson = computed(() => {
  return findRole("salesperson");
});

const csm = computed(() => {
  return findRole("csm");
});

const filteredRoles = computed(() => {
  return roles.value.filter((role) => role.value);
});

const rolesCondition = () => {
  superAdmin.value.value = isSuperAdmin.value;
  secretShopper.value.value = isSuperAdmin.value;

  accountManager.value.value = isAccountManager.value || isSuperAdmin.value;

  csm.value.value = false;

  //hide dealer manager for non automotive
  dealerManager.value.value = Boolean(currentUser.value.dealer.is_automotive);

  salesperson.value.value = true;
};
const findRole = (role) => {
  const foundRole = _find(roles.value, {
    name: role,
  });
  return foundRole || {};
};

const handleClickCancel = () => {
  if (isLoading.value) return;
  resetInput();
  emit("cancelEvent");
};

const handleClickSend = async () => {
  errorData.value = null;
  isLoading.value = true;

  axios
    .post("/api/v3/invite-users", {
      invite: inviteUsers.value,
    })
    .then(() => {
      emit("sendEvent");
    })
    .catch((error) => {
      if (error.response.status === 422) {
        showError.value = true;
        errorData.value = error.response.data;
      }
      Bugsnag.notify(error);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const resetInput = () => {
  inviteUsers.value = [
    {
      email: "",
      role_id: salesperson.value.id,
      dealer_id: currentUser.value.dealer_id,
      specific_dealer_id: currentUser.value.specific_dealer_id,
      specific_dealer_list: [],
    },
  ];
  getSpecificDealers();
  errorData.value = null;
};

watch(open, () => {
  if (open.value) {
    resetInput();
  }
});

const handleClickAdd = () => {
  inviteUsers.value.push({
    email: "",
    role_id: salesperson.value.id,
    dealer_id: currentUser.value.dealer_id,
    specific_dealer_id: currentUser.value.specific_dealer_id,
    specific_dealer_list: specificDealers.value,
  });
};

const handleClickRemove = (i) => {
  inviteUsers.value.splice(i, 1);
  errorValue.value = null;
};

const handleChangeRole = (user) => {
  if (!user) {
    return;
  }

  const requiresDealerManagerStorePick =
    user.role_id === dealerManager.value.id && !isSpecificManager.value;

  const requiresMsmLearnerStorePick =
    isMultiStoreManager.value && user.role_id === salesperson.value.id;

  if (requiresDealerManagerStorePick || requiresMsmLearnerStorePick) {
    user.specific_dealer_id = null;
  }
};

const handleChangeCompany = (newValue, index, invite) => {
  invite.specific_dealer_id = null;
  invite.specific_dealer_list = [];
  invite.specific_dealer_setting = {
    page: 1,
    per_page: 20,
    total: "",
  };
  getSpecificDealers(index, newValue);
};

watch(
  inviteUsers,
  () => {
    showError.value = false;
  },
  { deep: true }
);
</script>
